import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { hasUserAccessFunctionality, permissionSlugs } from 'helpers/PermissionHelper';
import { t } from 'i18next';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminPurchaseFormsState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
}

const visibleItems = hasUserAccessFunctionality([permissionSlugs.purchase_forms_list]);

const defaultColumnList = [
  {
    name: 'customer',
    label_key: 'order_#',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('customer') }),
    asyncKey: 'customer',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 1,
  },
  {
    name: 'product',
    label_key: 'product',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'payment_type',
    label_key: 'dates',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'ffl',
    label_key: 'seller_ffl',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('ffl') }),
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 4,
  },
  {
    name: 'shipping_ffl',
    label_key: 'shipping_to',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('shipping') }),
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 5,
  },
].filter(
  (col) =>
    visibleItems || (col.name !== 'ffl' && col.name !== 'customer' && col.name !== 'shipping_ffl')
);
const exportColumnList = [
  {
    name: 'customer',
    label_key: 'customer',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('customer') }),
    asyncKey: 'customer',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 1,
  },
  {
    name: 'product',
    label_key: 'product',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'payment_type',
    label_key: 'payment_type',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'ffl',
    label_key: 'seller_ffl',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('ffl') }),
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 4,
  },
  {
    name: 'shipping_ffl',
    label_key: 'shipping_to',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('shipping') }),
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 5,
  },
];
const initialState: AdminPurchaseFormsState = {
  excludedFilter: ['search'],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
};

const adminPurchaseFormsSlice = createSlice({
  name: 'adminPurchaseForms',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      console.log('filter data: ', action.payload);
      state.filterData = action.payload;
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setPurchaseFormsTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
  },
});

export const {
  setFilterData,
  removeFilterKey,
  resetFilterData,
  setReloadTableData,
  setPurchaseFormsTableColumnList,
} = adminPurchaseFormsSlice.actions;

export default adminPurchaseFormsSlice.reducer;
